import { useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { CUSTOMER_CHAT_REFRESH_INTERVAL_MS, SWR_KEYS } from 'constants/constants';
import { getOrderCustomerChatComments } from 'services/orders';
import { getLogisticServiceCustomerChatComments } from 'services/services';
import { ICustomerChatComment, TCustomerChatEntityType } from 'types/customerChat.types';
import { MENTION_TYPES } from 'types/dashboard.types';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';

interface IUseCustomerChatCommentsParams {
  mode: TCustomerChatEntityType;
  entityId?: number;
}

const fetchCustomerChatComments = async (
  mode: TCustomerChatEntityType,
  entityId: number
): Promise<ICustomerChatComment[]> => {
  const data =
    mode === MENTION_TYPES.ORDER
      ? await getOrderCustomerChatComments(entityId)
      : await getLogisticServiceCustomerChatComments(entityId);
  return Array.isArray(data) ? data : [];
};

/**
 * Carga y revalida los mensajes del chat con el cliente mientras el panel está montado
 * (tab de chat activa). El polling se detiene al desmontar el componente.
 */
const useCustomerChatComments = ({ mode, entityId }: IUseCustomerChatCommentsParams) => {
  const swrKey = useMemo(
    () => (entityId ? ([SWR_KEYS.customerChatComments, mode, entityId] as const) : null),
    [entityId, mode]
  );

  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    ([, chatMode, id]) => fetchCustomerChatComments(chatMode, id),
    {
      refreshInterval: CUSTOMER_CHAT_REFRESH_INTERVAL_MS,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 3000,
      keepPreviousData: true
    }
  );

  useEffect(() => {
    if (error && data === undefined) {
      displayErrorNotification({ customJSXMessage: <>No se pudo cargar el chat con el cliente</> });
    }
  }, [error, data]);

  const messages = data ?? [];
  const isLoadingList = isLoading && data === undefined;

  return { messages, isLoadingList, mutate };
};

export default useCustomerChatComments;
